/**
 * Maps icon string names from modules_entities.config.json to Lucide React components.
 * This enables JSON-based config while preserving React component usage.
 */

import React from 'react';
import {
  Home,
  GanttChartSquare,
  LayoutDashboard,
  Kanban,
  Layout,
  ChartNoAxesColumn,
  ClipboardList,
  Handshake,
  Briefcase,
  Contact,
  TrendingUp,
  TrendingDown,
  Filter,
  Landmark,
  GitFork,
  GitBranch,
  Receipt,
  Calendar,
  BarChart3,
  Users,
  Settings,
  User,
  Building2,
  Building,
  Network,
  History,
  ShieldCheck,
  Globe,
  Code,
  Combine,
  Bot,
  Video,
  Badge,
  BadgeDollarSign,
  CalendarDays,
  CalendarClock,
  CalendarX2,
  Terminal,
  MessageSquare,
  MessageSquareQuote,
  Key,
  MessagesSquare,
  Tag,
  Shield,
  Award,
  Gauge,
  ShoppingBag,
  Database,
  Layers,
  Tags,
  HeadsetIcon,
  Headset,
  ContactRound,
  Ticket,
  Inbox,
  BookOpen,
  Timer,
  Clock,
  AlarmClock,
  TriangleAlert,
  Fingerprint,
  QrCode,
  Star,
  FileText,
  FileCheck2,
  MessageCircleReply,
  Wand2,
  // Additional icons for tabs
  Info,
  Boxes,
  Shapes,
  DollarSign,
  Activity,
  StickyNote,
  FolderOpen,
  FolderKanban,
  Package,
  ChartLine,
  Lock,
  Mail,
  MailX,
  Plug,
  Scale,
  PieChart,
  ClipboardCheck,
  Sliders,
  Ship,
  Container,
  MapPin,
  Plane,
  Truck,
  Download,
  Upload,
  CreditCard,
  ShoppingCart,
  Percent,
  Map,
  Lightbulb,
  Newspaper,
  Flag,
  Palette,
  CalendarOff,
  HandCoins,
  Store,
  PackageCheck,
  FileInput,
  Warehouse,
  ArrowLeftRight,
  ClipboardEdit,
  Repeat,
  FileStack,
  Factory,
  CalendarCheck,
  ScrollText,
  ListOrdered,
  UserSearch,
  UserPlus,
  UserMinus,
  UserCheck,
  Rocket,
  Blocks,
  Target,
  Milestone,
  Megaphone,
  CircleDot,
  RefreshCcwDot,
  Bug,
  Radio,
  Radar,
  Paintbrush,
  UserCog,
  MousePointerClick,
  TextCursorInput,
  PanelTop,
  Table,
  List,
  Languages,
  Sun,
  LayoutGrid,
  Smartphone,
  Type,
  ALargeSmall,
  PanelLeft,
  SwatchBook,
  SquareCheckBig,
  AlertCircle,
  AlertTriangle,
  Cpu,
  Library,
  SlidersHorizontal,
  FileSignature,
  Send,
  Sparkles,
  Hourglass,
  Wallet,
  Undo2,
  Calculator,
  Gavel,
  Banknote,
  Folder,
  ArrowDownToLine,
  ArrowUpFromLine,
  Footprints,
  Workflow,
  MessageCircle,
  CircleOff,
  Crown,
  ArrowRight,
  RefreshCw,
  RotateCcw,
  BellRing,
  CheckCircle2,
  Zap,
  XCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

// Map of icon name strings to Lucide component references (for dynamic rendering with custom sizes)
const ICON_COMPONENT_MAP: Record<string, LucideIcon> = {
  Home, LayoutDashboard, Kanban, Layout, ClipboardList, ChartNoAxesColumn, Handshake, Briefcase,
  Contact, TrendingUp, TrendingDown, Filter, Landmark, GitFork, GitBranch, Receipt, Calendar,
  BarChart3, Users, Settings, User, Building2, Building, Network, History,
  ShieldCheck, Globe, Code, Bot, Video, Badge, CalendarDays, Terminal, MessageSquare,
  MessageSquareQuote,
  Key, MessagesSquare, Tag, Shield, ShoppingBag, Layers, Tags, HeadsetIcon, Database,
  Headset, ContactRound, Ticket, Inbox, BookOpen, Timer, Star, FileText, FileCheck2,
  MessageCircleReply, Wand2, Info, Boxes, Shapes, DollarSign, Activity,
  StickyNote, FolderOpen, FolderKanban, Package, ChartLine, Lock, Mail, MailX, Plug,
  Scale, PieChart, ClipboardCheck, Sliders, Ship, Container, MapPin, Plane, Truck,
  Download, Upload, CreditCard, ShoppingCart, Percent, Map, Lightbulb, Newspaper,
  Flag, Palette, CalendarOff, CalendarX2, HandCoins, Store, PackageCheck, FileInput,
  Warehouse, ArrowLeftRight, ClipboardEdit, Repeat, FileStack, Factory,
  CalendarCheck, ScrollText, ListOrdered, UserSearch, UserPlus, UserMinus, UserCheck, Rocket, Blocks, Target,
  Megaphone, CircleDot, RefreshCcwDot, Bug, Radio, Paintbrush,
  UserCog, MousePointerClick, TextCursorInput, PanelTop, Table, List,
  Languages, Sun, LayoutGrid, Smartphone, Type, ALargeSmall, PanelLeft, SwatchBook,
  SquareCheckBig, AlertCircle, AlertTriangle, Cpu, Library, SlidersHorizontal, FileSignature,
  Send, Sparkles, Hourglass, Wallet, GanttChartSquare,
  Undo2, Calculator, Gavel, Banknote, Folder, ArrowDownToLine, ArrowUpFromLine, Footprints,
  Clock, Fingerprint, QrCode, Workflow, MessageCircle,
  // Sync-policy role icons (sync_entity_roles.config.json) — used by SyncRoleBadge.
  CircleOff, Crown, ArrowRight, RefreshCw,
  // Reset / start-over icon (admin Fresh Start nav item).
  RotateCcw,
  BellRing, CheckCircle2, Zap, XCircle,
  // Skill-category icons (employee_skill_category.config.json) — used by SkillsSection.
  Award,
  // Material-name alias kept in sync with ICON_MAP so getIconComponent() resolves it too
  DirectionsRun: Footprints,
};

// Map of icon name strings to React components
const ICON_MAP: Record<string, React.ReactNode> = {
  Home: <Home className="h-4 w-4" />,
  Dashboard: <LayoutDashboard className="h-4 w-4" />,
  LayoutDashboard: <LayoutDashboard className="h-4 w-4" />,
  ViewKanban: <Kanban className="h-4 w-4" />,
  Workspaces: <Layout className="h-4 w-4" />,
  Assignment: <SquareCheckBig className="h-4 w-4" />,
  ClipboardList: <ClipboardList className="h-4 w-4" />,
  ChartNoAxesColumn: <ChartNoAxesColumn className="h-4 w-4" />,
  Handshake: <Handshake className="h-4 w-4" />,
  Business: <Building2 className="h-4 w-4" />,
  BusinessCenter: <Briefcase className="h-4 w-4" />,
  ContactPhone: <Contact className="h-4 w-4" />,
  TrendingUp: <TrendingUp className="h-4 w-4" />,
  TrendingDown: <TrendingDown className="h-4 w-4" />,
  FilterAlt: <Filter className="h-4 w-4" />,
  AccountBalance: <Landmark className="h-4 w-4" />,
  AccountTree: <GitFork className="h-4 w-4" />,
  Receipt: <Receipt className="h-4 w-4" />,
  CalendarMonth: <Calendar className="h-4 w-4" />,
  Assessment: <BarChart3 className="h-4 w-4" />,
  BarChart: <BarChart3 className="h-4 w-4" />,
  Groups: <Users className="h-4 w-4" />,
  Settings: <Settings className="h-4 w-4" />,
  People: <Users className="h-4 w-4" />,
  Person: <User className="h-4 w-4" />,
  CorporateFare: <Building2 className="h-4 w-4" />,
  GroupWork: <Network className="h-4 w-4" />,
  Network: <Network className="h-4 w-4" />,
  History: <History className="h-4 w-4" />,
  AdminPanelSettings: <ShieldCheck className="h-4 w-4" />,
  Domain: <Globe className="h-4 w-4" />,
  Globe: <Globe className="h-4 w-4" />,
  Wand2: <Wand2 className="h-4 w-4" />,
  Code: <Code className="h-4 w-4" />,
  SmartToy: <Bot className="h-4 w-4" />,
  Bot: <Bot className="h-4 w-4" />,
  Video: <Video className="h-4 w-4" />,
  Work: <Briefcase className="h-4 w-4" />,
  Badge: <Badge className="h-4 w-4" />,
  BadgeDollarSign: <BadgeDollarSign className="h-4 w-4" />,
  EventNote: <CalendarDays className="h-4 w-4" />,
  Api: <Terminal className="h-4 w-4" />,
  Chat: <MessageSquare className="h-4 w-4" />,
  Key: <Key className="h-4 w-4" />,
  Forum: <MessagesSquare className="h-4 w-4" />,
  Tag: <Tag className="h-4 w-4" />,
  LocationCity: <Building className="h-4 w-4" />,
  Shield: <Shield className="h-4 w-4" />,
  Award: <Award className="h-4 w-4" />,
  Gauge: <Gauge className="h-4 w-4" />,
  ShoppingBag: <ShoppingBag className="h-4 w-4" />,
  Category: <Layers className="h-4 w-4" />,
  PriceTag: <Tags className="h-4 w-4" />,
  Database: <Database className="h-4 w-4" />,
  // Support module icons
  SupportAgent: <HeadsetIcon className="h-4 w-4" />,
  Headset: <Headset className="h-4 w-4" />,
  GitBranch: <GitBranch className="h-4 w-4" />,
  ConfirmationNumber: <Ticket className="h-4 w-4" />,
  Inbox: <Inbox className="h-4 w-4" />,
  BellRing: <BellRing className="h-4 w-4" />,
  CheckCircle2: <CheckCircle2 className="h-4 w-4" />,
  Zap: <Zap className="h-4 w-4" />,
  XCircle: <XCircle className="h-4 w-4" />,
  MenuBook: <BookOpen className="h-4 w-4" />,
  Timer: <Timer className="h-4 w-4" />,
  AlarmClock: <AlarmClock className="h-4 w-4" />,
  TriangleAlert: <TriangleAlert className="h-4 w-4" />,
  Schedule: <Calendar className="h-4 w-4" />,
  SettingsInputComponent: <Settings className="h-4 w-4" />,
  Article: <FileText className="h-4 w-4" />,
  QuickReply: <MessageCircleReply className="h-4 w-4" />,
  // Direct name mappings for config navItems
  Building2: <Building2 className="h-4 w-4" />,
  ShieldCheck: <ShieldCheck className="h-4 w-4" />,
  Mail: <Mail className="h-4 w-4" />,
  MailX: <MailX className="h-4 w-4" />,
  Plug: <Plug className="h-4 w-4" />,
  MessageSquare: <MessageSquare className="h-4 w-4" />,
  MessageSquareQuote: <MessageSquareQuote className="h-4 w-4" />,
  FileText: <FileText className="h-4 w-4" />,
  FileCheck2: <FileCheck2 className="h-4 w-4" />,
  PieChart: <PieChart className="h-4 w-4" />,
  CalendarDays: <CalendarDays className="h-4 w-4" />,
  CalendarClock: <CalendarClock className="h-4 w-4" />,
  CalendarX2: <CalendarX2 className="h-4 w-4" />,
  UserCog: <UserCog className="h-4 w-4" />,
  Sliders: <Sliders className="h-4 w-4" />,
  User: <User className="h-4 w-4" />,
  MessageCircleReply: <MessageCircleReply className="h-4 w-4" />,
  ChartLine: <ChartLine className="h-4 w-4" />,
  MousePointerClick: <MousePointerClick className="h-4 w-4" />,
  TextCursorInput: <TextCursorInput className="h-4 w-4" />,
  PanelTop: <PanelTop className="h-4 w-4" />,
  Table: <Table className="h-4 w-4" />,
  List: <List className="h-4 w-4" />,
  Languages: <Languages className="h-4 w-4" />,
  Sun: <Sun className="h-4 w-4" />,
  LayoutGrid: <LayoutGrid className="h-4 w-4" />,
  Smartphone: <Smartphone className="h-4 w-4" />,
  Type: <Type className="h-4 w-4" />,
  ALargeSmall: <ALargeSmall className="h-4 w-4" />,
  PanelLeft: <PanelLeft className="h-4 w-4" />,
  Combine: <Combine className="h-4 w-4" />,
  SwatchBook: <SwatchBook className="h-4 w-4" />,
  // Contacts module icons
  Contacts: <ContactRound className="h-4 w-4" />,
  Company: <Building2 className="h-4 w-4" />,
  Star: <Star className="h-4 w-4" />,
  // Tab icons - standardized icons for tabs across the system
  Overview: <Info className="h-4 w-4" />,
  Variants: <Sliders className="h-4 w-4" />,
  Bundles: <Boxes className="h-4 w-4" />,
  Boxes: <Boxes className="h-4 w-4" />,
  Shapes: <Shapes className="h-4 w-4" />,
  Pricing: <DollarSign className="h-4 w-4" />,
  Activity: <Activity className="h-4 w-4" />,
  Notes: <StickyNote className="h-4 w-4" />,
  Files: <FolderOpen className="h-4 w-4" />,
  FolderKanban: <FolderKanban className="h-4 w-4" />,
  Messages: <MessageSquare className="h-4 w-4" />,
  Details: <FileText className="h-4 w-4" />,
  Documentation: <BookOpen className="h-4 w-4" />,
  Sdks: <Package className="h-4 w-4" />,
  Usage: <ChartLine className="h-4 w-4" />,
  Profile: <User className="h-4 w-4" />,
  Preferences: <Sliders className="h-4 w-4" />,
  Security: <Lock className="h-4 w-4" />,
  Email: <Mail className="h-4 w-4" />,
  Integrations: <Plug className="h-4 w-4" />,
  // Accounting report icons
  TrialBalance: <Scale className="h-4 w-4" />,
  BalanceSheet: <PieChart className="h-4 w-4" />,
  IncomeStatement: <TrendingUp className="h-4 w-4" />,
  GeneralLedger: <BookOpen className="h-4 w-4" />,
  // Recruiting icons
  Applications: <ClipboardCheck className="h-4 w-4" />,
  ClipboardCheck: <ClipboardCheck className="h-4 w-4" />,
  // Additional entity icons
  Boards: <Kanban className="h-4 w-4" />,
  Entries: <Receipt className="h-4 w-4" />,
  // Shipments module icons
  DollarSign: <DollarSign className="h-4 w-4" />,
  Ship: <Ship className="h-4 w-4" />,
  Container: <Container className="h-4 w-4" />,
  MapPin: <MapPin className="h-4 w-4" />,
  Plane: <Plane className="h-4 w-4" />,
  Truck: <Truck className="h-4 w-4" />,
  // Accounting module icons
  Download: <Download className="h-4 w-4" />,
  Upload: <Upload className="h-4 w-4" />,
  Description: <FileText className="h-4 w-4" />,
  Send: <Send className="h-4 w-4" />,
  Sparkles: <Sparkles className="h-4 w-4" />,
  Hourglass: <Hourglass className="h-4 w-4" />,
  Wallet: <Wallet className="h-4 w-4" />,
  // Contracts module
  FileSignature: <FileSignature className="h-4 w-4" />,
  Contract: <FileSignature className="h-4 w-4" />,
  Payments: <CreditCard className="h-4 w-4" />,
  ShoppingCart: <ShoppingCart className="h-4 w-4" />,
  Percent: <Percent className="h-4 w-4" />,
  Landmark: <Landmark className="h-4 w-4" />,
  Package: <Package className="h-4 w-4" />,
  Users: <Users className="h-4 w-4" />,
  // Roadmap module icons
  Map: <Map className="h-4 w-4" />,
  Lightbulb: <Lightbulb className="h-4 w-4" />,
  Newspaper: <Newspaper className="h-4 w-4" />,
  Flag: <Flag className="h-4 w-4" />,
  // Design System module icon
  Palette: <Palette className="h-4 w-4" />,
  Paintbrush: <Paintbrush className="h-4 w-4" />,
  // People module icons
  CalendarOff: <CalendarOff className="h-4 w-4" />,
  Clock: <Clock className="h-4 w-4" />,
  Fingerprint: <Fingerprint className="h-4 w-4" />,
  QrCode: <QrCode className="h-4 w-4" />,
  // Procurement module icons
  HandCoins: <HandCoins className="h-4 w-4" />,
  Store: <Store className="h-4 w-4" />,
  PackageCheck: <PackageCheck className="h-4 w-4" />,
  FileInput: <FileInput className="h-4 w-4" />,
  // Inventory module icons
  Warehouse: <Warehouse className="h-4 w-4" />,
  ArrowLeftRight: <ArrowLeftRight className="h-4 w-4" />,
  ClipboardEdit: <ClipboardEdit className="h-4 w-4" />,
  Repeat: <Repeat className="h-4 w-4" />,
  BarChart3: <BarChart3 className="h-4 w-4" />,
  // Accounting import icon
  FileStack: <FileStack className="h-4 w-4" />,
  // Sales Prospecting icons
  ListOrdered: <ListOrdered className="h-4 w-4" />,
  UserSearch: <UserSearch className="h-4 w-4" />,
  UserPlus: <UserPlus className="h-4 w-4" />,
  UserMinus: <UserMinus className="h-4 w-4" />,
  UserCheck: <UserCheck className="h-4 w-4" />,
  Rocket: <Rocket className="h-4 w-4" />,
  // Software Factory module icons
  Blocks: <Blocks className="h-4 w-4" />,
  Target: <Target className="h-4 w-4" />,
  GanttChartSquare: <GanttChartSquare className="h-4 w-4" />,
  Milestone: <Milestone className="h-4 w-4" />,
  Megaphone: <Megaphone className="h-4 w-4" />,
  CircleDot: <CircleDot className="h-4 w-4" />,
  RefreshCcwDot: <RefreshCcwDot className="h-4 w-4" />,
  Bug: <Bug className="h-4 w-4" />,
  Radio: <Radio className="h-4 w-4" />,
  Radar: <Radar className="h-4 w-4" />,
  // Software Factory module icons
  Factory: <Factory className="h-4 w-4" />,
  CalendarCheck: <CalendarCheck className="h-4 w-4" />,
  ScrollText: <ScrollText className="h-4 w-4" />,
  Filter: <Filter className="h-4 w-4" />,
  AlertCircle: <AlertCircle className="h-4 w-4" />,
  AlertTriangle: <AlertTriangle className="h-4 w-4" />,
  Cpu: <Cpu className="h-4 w-4" />,
  Library: <Library className="h-4 w-4" />,
  SlidersHorizontal: <SlidersHorizontal className="h-4 w-4" />,
  Layers: <Layers className="h-4 w-4" />,
  // ERP cluster entity / nav icons
  Undo2: <Undo2 className="h-4 w-4" />,
  Calculator: <Calculator className="h-4 w-4" />,
  Gavel: <Gavel className="h-4 w-4" />,
  Banknote: <Banknote className="h-4 w-4" />,
  Folder: <Folder className="h-4 w-4" />,
  ArrowDownToLine: <ArrowDownToLine className="h-4 w-4" />,
  ArrowUpFromLine: <ArrowUpFromLine className="h-4 w-4" />,
  Kanban: <Kanban className="h-4 w-4" />,
  Calendar: <Calendar className="h-4 w-4" />,
  CreditCard: <CreditCard className="h-4 w-4" />,
  BookOpen: <BookOpen className="h-4 w-4" />,
  DirectionsRun: <Footprints className="h-4 w-4" />,
  // Billing module icon
  Scale: <Scale className="h-4 w-4" />,
  // Chatbot module icons
  MessagesSquare: <MessagesSquare className="h-4 w-4" />,
  Workflow: <Workflow className="h-4 w-4" />,
  MessageCircle: <MessageCircle className="h-4 w-4" />,
  // Sync-policy role icons (sync_entity_roles.config.json).
  CircleOff: <CircleOff className="h-4 w-4" />,
  Crown: <Crown className="h-4 w-4" />,
  ArrowRight: <ArrowRight className="h-4 w-4" />,
  RefreshCw: <RefreshCw className="h-4 w-4" />,
  // Reset / start-over icon (admin Fresh Start nav item).
  RotateCcw: <RotateCcw className="h-4 w-4" />,
};

/**
 * Get a React icon component by its string name.
 * Returns undefined if the icon name is not found.
 */
export function getIcon(iconName: string | undefined): React.ReactNode | undefined {
  if (!iconName) return undefined;
  return ICON_MAP[iconName];
}

/**
 * Get a Lucide icon component by its string name.
 * Returns the component itself (not a rendered element), so callers can pass props like className.
 * Returns undefined if the icon name is not found.
 */
export function getIconComponent(iconName: string | undefined): LucideIcon | undefined {
  if (!iconName) return undefined;
  return ICON_COMPONENT_MAP[iconName];
}

/**
 * Get all available icon names.
 */
export function getAvailableIconNames(): string[] {
  return Object.keys(ICON_MAP);
}
